/**
 * Epic 10 glue, mounted once in the shell.
 * - US 10.1: reading a Tasks item in the global bell reads its Tasks Inbox
 *   item too (the reverse — Inbox → bell — is done by the server).
 * - US 10.1: any Tasks notification arriving live refreshes the Inbox list
 *   and its unread counts, so a new item shows without reloading.
 * - US 10.3: the first time a reminder or an assignment arrives, offer to
 *   turn on browser notifications; the person can allow or refuse.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { NOTIFICATIONS_READ_EVENT, useLiveNotifications, type AppNotification } from "@weco/suite-chrome";
import { osPermission, requestOsPermission } from "../../../lib/notifications/osNotification";
import { ME_KEY } from "../personal/keys";
import { inboxApi } from "./api";

const ASKED_KEY = "weco.tasks.browserNotificationsAsked";

export function inboxIdsOf(items: AppNotification[]): string[] {
  return items.map((n) => (n.data as Record<string, unknown> | undefined)?.inbox_item).filter((x): x is string => typeof x === "string" && !!x);
}

export function TasksNotifySync() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  useEffect(() => {
    const onRead = (e: Event) => {
      const ids = inboxIdsOf(((e as CustomEvent).detail as AppNotification[]) ?? []);
      if (!ids.length) return;
      inboxApi.bulk(ids, "read").then(
        () => void qc.invalidateQueries({ queryKey: [...ME_KEY, "inbox"] }),
        () => undefined,
      );
    };
    window.addEventListener(NOTIFICATIONS_READ_EVENT, onRead);
    return () => window.removeEventListener(NOTIFICATIONS_READ_EVENT, onRead);
  }, [qc]);
  useLiveNotifications((n) => {
    if (!n.kind.startsWith("tasks.")) return;
    // US 10.1 — the new item tops the Inbox and the counts move within ~2s.
    void qc.invalidateQueries({ queryKey: [...ME_KEY, "inbox"] });
    if (n.kind !== "tasks.reminder" && n.kind !== "tasks.assigned") return;
    let asked = false;
    try {
      asked = localStorage.getItem(ASKED_KEY) === "1";
    } catch {
      /* ask again next session */
    }
    if (asked || osPermission() !== "default") return;
    try {
      localStorage.setItem(ASKED_KEY, "1");
    } catch {
      /* fine */
    }
    toast(t("tasks.notify.offerTitle", { defaultValue: "Get browser notifications for reminders and assignments?" }), {
      duration: 15_000,
      action: { label: t("tasks.notify.turnOn", { defaultValue: "Turn on" }), onClick: () => void requestOsPermission() },
      cancel: { label: t("tasks.notify.notNow", { defaultValue: "Not now" }), onClick: () => undefined },
    });
  }, true);
  return null;
}
